import { useState, useMemo } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAtomValue } from 'jotai'
import { riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
import { WorkspacesTable } from '../components/workspaces-table'
import { RiskWorkspace } from '@app/types'
import { Button } from '@/components/ui/button'
import { PlusIcon } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { appLink } from '@app/config/appLink'
import { workspaceHasNoValidLayers } from '@app/components/Risk/Workspaces/utils/Workspaces.utils'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'

export const SavedWorkspacesPage = () => {
  const navigate = useNavigate()
  const { layerData } = useSelector((state: RootState) => state.riskMap)

  const [workspaceAccess, setWorkspaceAccess] = useState<'private' | 'organisation'>('private')
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null)

  const {
    data: workspaces,
    isPending,
    isLoading,
    isError,
  } = useAtomValue(riskAvailableWorkspacesAtom)

  const filteredWorkspaces = useMemo(() => {
    if (isPending || isLoading || isError) null

    if (workspaces?.length) {
      return workspaces?.filter((workspace) => workspace.visibility === workspaceAccess)
    }
  }, [workspaces, isPending, isLoading, isError, workspaceAccess])

  const handleLoadWorkspace = () => {
    if (!workspaces) return
    const workspace = workspaces.find((workspace) => workspace.id === selectedWorkspace)
    if (!workspace) return

    if (
      !selectedWorkspace ||
      !layerData ||
      workspaceHasNoValidLayers(workspace, layerData) ||
      !workspaces
    )
      return

    // loads the workspace from the useLoadWorkspaceFromURL hook on the risk page
    navigate(`${appLink.riskExplorer.path}?workspace=${workspace.id}`)
  }

  return (
    <div className="flex items-center flex-col">
      <div className="p-8 max-w-[1240px] w-full">
        {/* Header */}
        <h1 className="text-3xl font-semibold mb-2 text-rexBlue-900">Workspaces</h1>
        <p className="w-2/3">
          Use Workspaces to manage which controls are shown in Risk map and Profile. Create a new
          Workspace, or edit an existing one below.{' '}
        </p>
        <Tabs className="w-full mt-6" defaultValue="private">
          <div className="flex items-center justify-between">
            <TabsList className="w-1/3 min-w-[250px]">
              <TabsTrigger
                className="w-1/2"
                value="private"
                onClick={() => {
                  setWorkspaceAccess('private')
                  setSelectedWorkspace(null)
                }}
              >
                Personal
              </TabsTrigger>
              <TabsTrigger
                className="w-1/2"
                value="organisation"
                onClick={() => {
                  setWorkspaceAccess('organisation')
                  setSelectedWorkspace(null)
                }}
              >
                Organisation
              </TabsTrigger>
            </TabsList>
            <Link to={appLink.workspacesCreate.path}>
              <Button variant="outline">
                <PlusIcon /> Create new
              </Button>
            </Link>
          </div>
          <TabsContent className="text-sm" value="private">
            <WorkspacesTable
              selectedWorkspace={selectedWorkspace}
              setSelectedWorkspace={setSelectedWorkspace}
              workspaces={filteredWorkspaces as RiskWorkspace[]}
            />
          </TabsContent>
          <TabsContent className="text-sm" value="organisation">
            <WorkspacesTable
              selectedWorkspace={selectedWorkspace}
              setSelectedWorkspace={setSelectedWorkspace}
              workspaces={filteredWorkspaces as RiskWorkspace[]}
            />
          </TabsContent>
        </Tabs>
      </div>
      <div className="bg-white w-full flex justify-center absolute bottom-0 border-t">
        <div className="max-w-[1240px] w-full px-8 py-4">
          <div className="flex justify-end">
            <Button onClick={handleLoadWorkspace} disabled={Boolean(!selectedWorkspace)}>
              Apply Workspace
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
